import { 
  HubConnection, 
  HubConnectionBuilder, 
  HubConnectionState, 
  LogLevel 
} from '@microsoft/signalr';
import { 
  SignalREventHandlers, 
  SignalRConfig, 
  ConnectionInfo, 
  ConnectionState,
  WorkStatusEvent,
  BreakStatusEvent,
  TeamUpdateEvent,
  UserStatusEvent,
  SystemNotificationEvent,
  AlertEvent,
  CommentEvent,
  ConnectionStatusEvent
} from '../types/signalRTypes';

const DEFAULT_CONFIG: SignalRConfig = {
  hubUrl: '/hubs/worktracking',
  automaticReconnect: true,
  reconnectAttempts: [0, 2000, 5000, 10000, 30000], // More aggressive initial reconnects
  keepAliveInterval: 15000, // Keep alive ping every 15 seconds
  serverTimeout: 90000, // 90 seconds - aligned with server timeout
  enableDetailedErrors: process.env.NODE_ENV === 'development',
  skipNegotiation: false,
  transport: undefined
};

export class SignalRService {
  private connection: HubConnection | null = null;
  private config: SignalRConfig;
  private eventHandlers: SignalREventHandlers = {};
  private connectionInfo: ConnectionInfo | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private isManualDisconnect = false;
  private connectionTimeout: NodeJS.Timeout | null = null;
  private keepAliveInterval: NodeJS.Timeout | null = null;
  private lastConnectionAttempt = 0;
  private minConnectionInterval = 2000; // Minimum 2 seconds between connection attempts
  private isConnecting = false; // Flag to prevent multiple simultaneous connection attempts
  private deviceId: string | null = null; // Device identifier
  private currentUserId: string | null = null; // Current user ID
  private currentUserRole: string | null = null; // Current user role
  
  // Event listeners
  private onConnectionStateChanged: ((state: ConnectionState) => void) | null = null;
  private onError: ((error: Error) => void) | null = null;

  constructor(config: Partial<SignalRConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    
    // Get the API base URL from environment or default
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'https://localhost:7000/api';
    // Remove /api from the end to get base URL
    const hubBaseUrl = apiBaseUrl.replace(/\/api\/?$/, '');
    // Ensure we don't have double slashes
    this.config.hubUrl = `${hubBaseUrl}${this.config.hubUrl}`;
    console.log('SignalR: Hub URL configured as', this.config.hubUrl);
    
    // Generate or retrieve device ID
    this.deviceId = this.getOrCreateDeviceId();
  }

  /**
   * Initialize and start the SignalR connection
   */
  public async connect(userId: string, userRole: string): Promise<void> {
    // Store user info for connection
    this.currentUserId = userId;
    this.currentUserRole = userRole;
    
    // Check if already connected
    if (this.connection && this.connection.state === HubConnectionState.Connected) {
      console.log('SignalR: Already connected');
      return;
    }

    // Check if connecting
    if (this.connection && this.connection.state === HubConnectionState.Connecting) {
      console.log('SignalR: Connection in Connecting state, waiting...');
      return;
    }

    // Prevent multiple simultaneous connection attempts
    if (this.isConnecting) {
      console.log('SignalR: Connection attempt already in progress');
      return;
    }

    // Prevent too frequent connection attempts
    const now = Date.now();
    if (now - this.lastConnectionAttempt < this.minConnectionInterval) {
      console.log('SignalR: Connection attempt too soon, waiting...');
      await new Promise(resolve => setTimeout(resolve, this.minConnectionInterval - (now - this.lastConnectionAttempt)));
    }
    this.lastConnectionAttempt = Date.now();
    this.isConnecting = true;

    // Check if hub endpoint exists first in production
    if (import.meta.env.PROD) {
      try {
        const testUrl = this.config.hubUrl.replace('/hubs/worktracking', '/api/health');
        const response = await fetch(testUrl);
        if (!response.ok) {
          console.warn('SignalR: Backend health check failed, hub may not be available');
        }
      } catch (error) {
        console.warn('SignalR: Cannot reach backend, SignalR will be disabled in this session');
        return;
      }
    }

    try {
      this.isManualDisconnect = false;
      
      // Clear any existing connection timeout
      if (this.connectionTimeout) {
        clearTimeout(this.connectionTimeout);
        this.connectionTimeout = null;
      }
      
      this.connection = this.createConnection();
      
      // Set up event handlers
      this.setupEventHandlers();
      
      // Set up connection state listeners
      this.setupConnectionStateListeners();
      
      console.log('SignalR: Starting connection...');
      
      // Add connection timeout to prevent hanging
      const connectionPromise = this.connection.start();
      this.connectionTimeout = setTimeout(() => {
        console.error('SignalR: Connection timeout');
        this.isConnecting = false;
        this.connection?.stop();
        // Notify timeout error
        this.notifyError(new Error('SignalR connection timeout'));
      }, 15000); // 15 second timeout
      
      await connectionPromise;
      
      // Clear timeout on successful connection
      if (this.connectionTimeout) {
        clearTimeout(this.connectionTimeout);
        this.connectionTimeout = null;
      }
      
      // Store connection info
      this.connectionInfo = {
        connectionId: this.connection.connectionId || '',
        userId,
        role: userRole,
        isConnected: true,
        connectedAt: new Date(),
        lastActivity: new Date()
      };
      
      console.log('SignalR: Connection established', this.connectionInfo);
      
      // Wait a bit for connection to be fully ready before joining groups
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Join user and role groups with better error handling
      try {
        await this.joinUserGroup(userId);
        await this.joinRoleGroup(userRole);
      } catch (groupError) {
        console.warn('SignalR: Failed to join groups, but connection is established:', groupError);
        // Don't fail the entire connection for group join errors
      }
      
      // Reset reconnect attempts on successful connection
      this.reconnectAttempts = 0;
      
      // Reset connecting flag
      this.isConnecting = false;
      
      // Start keep-alive ping to prevent timeouts
      this.startKeepAlive();
      
      // Notify connection state change
      this.notifyConnectionStateChange('Connected');
      
    } catch (error) {
      // Clear timeout on error
      if (this.connectionTimeout) {
        clearTimeout(this.connectionTimeout);
        this.connectionTimeout = null;
      }
      
      // Reset connecting flag on error
      this.isConnecting = false;
      
      console.error('SignalR: Connection failed', error);
      
      // In production, don't throw error to prevent app crashes
      if (import.meta.env.PROD) {
        console.warn('SignalR: Connection failed in production, continuing without real-time updates');
        this.notifyError(error as Error);
        return;
      }
      
      this.notifyError(error as Error);
      throw error;
    }
  }

  /**
   * Disconnect from SignalR hub
   */
  public async disconnect(): Promise<void> {
    if (!this.connection) {
      return;
    }

    try {
      this.isManualDisconnect = true;
      console.log('SignalR: Disconnecting...');
      
      // Clear any pending connection timeout
      if (this.connectionTimeout) {
        clearTimeout(this.connectionTimeout);
        this.connectionTimeout = null;
      }
      
      // Stop keep-alive
      this.stopKeepAlive();
      
      await this.connection.stop();
      this.connection = null;
      this.connectionInfo = null;
      
      console.log('SignalR: Disconnected');
      this.notifyConnectionStateChange('Disconnected');
      
    } catch (error) {
      console.error('SignalR: Disconnect failed', error);
      this.notifyError(error as Error);
    }
  }

  /**
   * Get current connection state
   */
  public getConnectionState(): ConnectionState {
    if (!this.connection) {
      return 'Disconnected';
    }
    
    switch (this.connection.state) {
      case HubConnectionState.Disconnected:
        return 'Disconnected';
      case HubConnectionState.Connecting:
        return 'Connecting';
      case HubConnectionState.Connected:
        return 'Connected';
      case HubConnectionState.Disconnecting:
        return 'Disconnecting';
      case HubConnectionState.Reconnecting:
        return 'Reconnecting';
      default:
        return 'Disconnected';
    }
  }

  /**
   * Check if connection is active
   */
  public isConnected(): boolean {
    return this.connection?.state === HubConnectionState.Connected;
  }
  
  /**
   * Check if SignalR should be enabled in current environment
   */
  public shouldUseSignalR(): boolean {
    // Always try to use SignalR unless explicitly disabled
    const signalREnabled = import.meta.env.VITE_SIGNALR_ENABLED !== 'false';
    
    if (!signalREnabled) {
      console.warn('SignalR is explicitly disabled via VITE_SIGNALR_ENABLED=false');
    }
    
    return signalREnabled;
  }

  /**
   * Get connection information
   */
  public getConnectionInfo(): ConnectionInfo | null {
    return this.connectionInfo;
  }

  /**
   * Set event handlers
   */
  public setEventHandlers(handlers: SignalREventHandlers): void {
    this.eventHandlers = { ...this.eventHandlers, ...handlers };
  }

  /**
   * Set connection state change listener
   */
  public setConnectionStateListener(listener: (state: ConnectionState) => void): void {
    this.onConnectionStateChanged = listener;
  }

  /**
   * Set error listener
   */
  public setErrorListener(listener: (error: Error) => void): void {
    this.onError = listener;
  }

  /**
   * Join a team group
   */
  public async joinTeamGroup(teamId: string): Promise<void> {
    if (!this.connection || this.connection.state !== HubConnectionState.Connected) {
      throw new Error('SignalR: Not connected');
    }

    try {
      await this.connection.invoke('JoinTeamGroup', teamId);
      console.log(`SignalR: Joined team group ${teamId}`);
    } catch (error) {
      console.error(`SignalR: Failed to join team group ${teamId}`, error);
      throw error;
    }
  }

  /**
   * Leave a team group
   */
  public async leaveTeamGroup(teamId: string): Promise<void> {
    if (!this.connection || this.connection.state !== HubConnectionState.Connected) {
      throw new Error('SignalR: Not connected');
    }

    try {
      await this.connection.invoke('LeaveTeamGroup', teamId);
      console.log(`SignalR: Left team group ${teamId}`);
    } catch (error) {
      console.error(`SignalR: Failed to leave team group ${teamId}`, error);
      throw error;
    }
  }

  /**
   * Join a project group
   */
  public async joinProjectGroup(projectId: string): Promise<void> {
    if (!this.connection || this.connection.state !== HubConnectionState.Connected) {
      throw new Error('SignalR: Not connected');
    }

    try {
      await this.connection.invoke('JoinProjectGroup', projectId);
      console.log(`SignalR: Joined project group ${projectId}`);
    } catch (error) {
      console.error(`SignalR: Failed to join project group ${projectId}`, error);
      throw error;
    }
  }

  /**
   * Leave a project group
   */
  public async leaveProjectGroup(projectId: string): Promise<void> {
    if (!this.connection || this.connection.state !== HubConnectionState.Connected) {
      throw new Error('SignalR: Not connected');
    }

    try {
      await this.connection.invoke('LeaveProjectGroup', projectId);
      console.log(`SignalR: Left project group ${projectId}`);
    } catch (error) {
      console.error(`SignalR: Failed to leave project group ${projectId}`, error);
      throw error;
    }
  }

  /**
   * Send a ping to test connection
   */
  public async ping(): Promise<void> {
    if (!this.connection || this.connection.state !== HubConnectionState.Connected) {
      throw new Error('SignalR: Not connected');
    }

    try {
      await this.connection.invoke('Ping');
      console.log('SignalR: Ping sent');
    } catch (error) {
      console.error('SignalR: Ping failed', error);
      throw error;
    }
  }

  /**
   * Create SignalR connection
   */
  private createConnection(): HubConnection {
    console.log('SignalR: Creating connection to', this.config.hubUrl);
    
    // Build query parameters
    const queryParams = new URLSearchParams();
    queryParams.append('deviceId', this.deviceId || 'unknown');
    if (this.currentUserId) {
      queryParams.append('userId', this.currentUserId);
    }
    if (this.currentUserRole) {
      queryParams.append('userRole', this.currentUserRole);
    }
    
    const urlWithParams = `${this.config.hubUrl}?${queryParams.toString()}`;
    
    const connectionOptions: { headers: Record<string, string>; [key: string]: unknown } = {
      headers: {
        'X-Device-Id': this.deviceId || 'unknown',
        'X-User-Id': this.currentUserId || '',
        'X-User-Role': this.currentUserRole || ''
      }
    };
    
    // Add additional options for production environment
    if (import.meta.env.PROD) {
      connectionOptions.withCredentials = false;
      connectionOptions.accessTokenFactory = undefined;
      // Use automatic transport selection in production
      connectionOptions.skipNegotiation = false;
      connectionOptions.transport = undefined; // Let SignalR choose the best transport
    }
    
    const builder = new HubConnectionBuilder()
      .withUrl(urlWithParams, connectionOptions)
      .configureLogging(this.config.enableDetailedErrors ? LogLevel.Debug : LogLevel.Error);

    // Configure automatic reconnect with production-safe settings
    if (this.config.automaticReconnect && this.config.reconnectAttempts) {
      builder.withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (retryContext) => {
          // Custom retry logic based on error type and attempt count
          if (retryContext.elapsedMilliseconds > 300000) {
            // Stop retrying after 5 minutes
            return null;
          }
          
          // Use configured delays
          const attemptIndex = Math.min(retryContext.previousRetryCount, this.config.reconnectAttempts!.length - 1);
          return this.config.reconnectAttempts![attemptIndex];
        }
      });
    }

    const connection = builder.build();
    
    // Configure connection timeouts after building
    // These need to be set on the connection object directly
    if (this.config.keepAliveInterval) {
      (connection as HubConnection & { keepAliveIntervalInMilliseconds?: number }).keepAliveIntervalInMilliseconds = this.config.keepAliveInterval;
    }
    
    if (this.config.serverTimeout) {
      (connection as HubConnection & { serverTimeoutInMilliseconds?: number }).serverTimeoutInMilliseconds = this.config.serverTimeout;
    }

    return connection;
  }

  /**
   * Setup event handlers for SignalR events
   */
  private setupEventHandlers(): void {
    if (!this.connection) return;

    // Work entry events
    this.connection.on('WorkStarted', (event: WorkStatusEvent) => {
      console.log('SignalR: WorkStarted', event);
      this.eventHandlers.onWorkStarted?.(event);
    });

    this.connection.on('WorkEnded', (event: WorkStatusEvent) => {
      console.log('SignalR: WorkEnded', event);
      this.eventHandlers.onWorkEnded?.(event);
    });

    this.connection.on('WorkUpdated', (event: WorkStatusEvent) => {
      console.log('SignalR: WorkUpdated', event);
      this.eventHandlers.onWorkUpdated?.(event);
    });

    // Break events
    this.connection.on('BreakStarted', (event: BreakStatusEvent) => {
      console.log('SignalR: BreakStarted', event);
      this.eventHandlers.onBreakStarted?.(event);
    });

    this.connection.on('BreakEnded', (event: BreakStatusEvent) => {
      console.log('SignalR: BreakEnded', event);
      this.eventHandlers.onBreakEnded?.(event);
    });

    this.connection.on('BreakUpdated', (event: BreakStatusEvent) => {
      console.log('SignalR: BreakUpdated', event);
      this.eventHandlers.onBreakUpdated?.(event);
    });

    // Team events
    this.connection.on('TeamMemberStatusChanged', (event: TeamUpdateEvent) => {
      console.log('SignalR: TeamMemberStatusChanged', event);
      this.eventHandlers.onTeamMemberStatusChanged?.(event);
    });

    this.connection.on('TeamCompositionChanged', (event: TeamUpdateEvent) => {
      console.log('SignalR: TeamCompositionChanged', event);
      this.eventHandlers.onTeamCompositionChanged?.(event);
    });

    this.connection.on('TeamPerformanceUpdated', (event: TeamUpdateEvent) => {
      console.log('SignalR: TeamPerformanceUpdated', event);
      this.eventHandlers.onTeamPerformanceUpdated?.(event);
    });

    // User events
    this.connection.on('UserStatusChanged', (event: UserStatusEvent) => {
      console.log('SignalR: UserStatusChanged', event);
      this.eventHandlers.onUserStatusChanged?.(event);
    });

    this.connection.on('UserCreated', (event: UserStatusEvent) => {
      console.log('SignalR: UserCreated', event);
      this.eventHandlers.onUserCreated?.(event);
    });

    this.connection.on('UserUpdated', (event: UserStatusEvent) => {
      console.log('SignalR: UserUpdated', event);
      this.eventHandlers.onUserUpdated?.(event);
    });

    this.connection.on('UserDeleted', (event: { id: number; timestamp: string }) => {
      console.log('SignalR: UserDeleted', event);
      this.eventHandlers.onUserDeleted?.(event);
    });

    // System events
    this.connection.on('SystemNotification', (event: SystemNotificationEvent) => {
      console.log('SignalR: SystemNotification', event);
      this.eventHandlers.onSystemNotification?.(event);
    });

    this.connection.on('AlertTriggered', (event: AlertEvent) => {
      console.log('SignalR: AlertTriggered', event);
      this.eventHandlers.onAlertTriggered?.(event);
    });

    // Comment events
    this.connection.on('CommentAdded', (event: CommentEvent) => {
      console.log('SignalR: CommentAdded', event);
      this.eventHandlers.onCommentAdded?.(event);
    });

    // Connection events
    this.connection.on('ConnectionStatusChanged', (event: ConnectionStatusEvent) => {
      console.log('SignalR: ConnectionStatusChanged', event);
      this.eventHandlers.onConnectionStatusChanged?.(event);
    });

    this.connection.on('ForceDisconnect', (reason: string) => {
      console.log('SignalR: ForceDisconnect', reason);
      this.eventHandlers.onForceDisconnect?.(reason);
    });
  }

  /**
   * Setup connection state listeners
   */
  private setupConnectionStateListeners(): void {
    if (!this.connection) return;

    this.connection.onclose((error) => {
      console.log('SignalR: Connection closed', error);
      this.connectionInfo = null;
      this.notifyConnectionStateChange('Disconnected');
      
      // Only report errors if it's not a manual disconnect and not in production
      if (error && !this.isManualDisconnect) {
        if (import.meta.env.DEV) {
          this.notifyError(error);
        } else {
          console.warn('SignalR: Connection closed in production', error.message);
        }
      }
    });

    this.connection.onreconnecting((error) => {
      console.log('SignalR: Reconnecting...', error);
      this.reconnectAttempts++;
      
      // Stop trying to reconnect after max attempts in production
      if (import.meta.env.PROD && this.reconnectAttempts >= this.maxReconnectAttempts) {
        console.warn('SignalR: Max reconnection attempts reached in production, disabling SignalR');
        this.connection?.stop();
        return;
      }
      
      this.notifyConnectionStateChange('Reconnecting');
      
      // Only report reconnection errors in development
      if (error && import.meta.env.DEV) {
        this.notifyError(error);
      }
    });

    this.connection.onreconnected((connectionId) => {
      console.log('SignalR: Reconnected', connectionId);
      this.reconnectAttempts = 0;
      
      if (this.connectionInfo) {
        this.connectionInfo.connectionId = connectionId || '';
        this.connectionInfo.isConnected = true;
        this.connectionInfo.lastActivity = new Date();
      }
      
      this.notifyConnectionStateChange('Connected');
    });
  }

  /**
   * Join user group
   */
  private async joinUserGroup(userId: string): Promise<void> {
    if (!this.connection || this.connection.state !== HubConnectionState.Connected) {
      console.warn(`SignalR: Cannot join user group - connection not ready. State: ${this.connection?.state}`);
      return; // Don't throw error, just return
    }

    try {
      // The hub automatically joins users to their user group on connection
      console.log(`SignalR: User ${userId} joined user group automatically`);
    } catch (error) {
      console.error(`SignalR: Failed to join user group for ${userId}`, error);
      // Don't throw error to prevent connection loop
    }
  }

  /**
   * Join role group
   */
  private async joinRoleGroup(userRole: string): Promise<void> {
    if (!this.connection || this.connection.state !== HubConnectionState.Connected) {
      console.warn(`SignalR: Cannot join role group - connection not ready. State: ${this.connection?.state}`);
      return; // Don't throw error, just return
    }

    try {
      // The hub automatically joins users to their role group on connection
      console.log(`SignalR: User joined role group ${userRole} automatically`);
    } catch (error) {
      console.error(`SignalR: Failed to join role group ${userRole}`, error);
      // Don't throw error to prevent connection loop
    }
  }

  /**
   * Notify connection state change
   */
  private notifyConnectionStateChange(state: ConnectionState): void {
    this.onConnectionStateChanged?.(state);
  }

  /**
   * Notify error
   */
  private notifyError(error: Error): void {
    this.onError?.(error);
  }

  /**
   * Start keep-alive ping to prevent timeouts
   */
  private startKeepAlive(): void {
    // Clear any existing keep-alive interval
    this.stopKeepAlive();
    
    // Only start keep-alive if we have a connection
    if (!this.connection || this.connection.state !== HubConnectionState.Connected) {
      return;
    }
    
    console.log('SignalR: Starting keep-alive mechanism');
    
    // Send a ping every 15 seconds to keep the connection alive
    // This is more frequent than the server's 30-second keep-alive to ensure we don't timeout
    this.keepAliveInterval = setInterval(async () => {
      if (this.connection && this.connection.state === HubConnectionState.Connected) {
        try {
          // Try to invoke KeepAlive method first (from improved hub)
          await this.connection.invoke('KeepAlive');
          
          // Update last activity
          if (this.connectionInfo) {
            this.connectionInfo.lastActivity = new Date();
          }
        } catch (error) {
          try {
            // Fallback to Ping method if KeepAlive is not available
            await this.connection.invoke('Ping');
            
            // Update last activity
            if (this.connectionInfo) {
              this.connectionInfo.lastActivity = new Date();
            }
          } catch (pingError) {
            console.warn('SignalR: Keep-alive failed, connection may be lost', pingError);
          }
        }
      } else {
        // Stop keep-alive if connection is no longer active
        console.log('SignalR: Connection no longer active, stopping keep-alive');
        this.stopKeepAlive();
      }
    }, 15000); // 15 seconds - more frequent than server's keep-alive
  }

  /**
   * Stop keep-alive ping
   */
  private stopKeepAlive(): void {
    if (this.keepAliveInterval) {
      clearInterval(this.keepAliveInterval);
      this.keepAliveInterval = null;
    }
  }
  
  /**
   * Get or create a device ID for this browser/device
   */
  private getOrCreateDeviceId(): string {
    const storageKey = 'signalr_device_id';
    let deviceId = localStorage.getItem(storageKey);
    
    if (!deviceId) {
      // Generate a unique device ID
      deviceId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem(storageKey, deviceId);
    }
    
    return deviceId;
  }
}

// Export singleton instance with lazy initialization
let _signalRService: SignalRService | null = null;

export const signalRService = (() => {
  if (!_signalRService) {
    _signalRService = new SignalRService();
  }
  return _signalRService;
})();